/** Pay the subscription from the login screen when the shop is locked (owner only). */
import { useEffect, useState } from 'react';

import api, { errorMessage } from '../services/api';
import { formatINR } from '../utils/money';
import { ErrorBox, Field, Modal, Spinner } from './ui';

interface Invoice { id: number; invoice_number: string; period_label: string; amount_paise: number; status: string }
interface Position { amount_due_paise: number; invoices: Invoice[]; upi_id: string | null; payee_name: string | null;
  bank_details: string | null; qr_image: string | null; upi_link: string | null; shop_name: string; pay_by: string | null }

export default function LockedOutPay({ open, onClose, creds }: {
  open: boolean; onClose: () => void; creds: { username: string; password: string };
}) {
  const [pos, setPos] = useState<Position | null>(null);
  const [error, setError] = useState('');
  const [picked, setPicked] = useState<number[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [utr, setUtr] = useState('');
  const [done, setDone] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const f = new FormData();
    Object.entries(creds).forEach(([k, v]) => f.append(k, v));
    api.post<Position>('/subscription/locked-out', f, { headers: { 'Content-Type': 'multipart/form-data' } })
      .then(({ data }) => { setPos(data); setPicked(data.invoices.filter((i) => i.status === 'pending').map((i) => i.id)); })
      .catch((e) => setError(errorMessage(e)));
  }, [open, creds]);

  const amount = pos?.invoices.filter((i) => picked.includes(i.id)).reduce((a, i) => a + i.amount_paise, 0) || 0;

  const submit = async () => {
    if (!file) { setError('Attach the payment screenshot'); return; }
    setBusy(true); setError('');
    const f = new FormData();
    Object.entries(creds).forEach(([k, v]) => f.append(k, v));
    f.append('invoice_ids', picked.join(','));
    f.append('amount_paise', String(amount));
    f.append('reference_no', utr);
    f.append('file', file);
    try {
      const { data } = await api.post('/subscription/locked-out/payment', f, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDone(data.message);
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Pay subscription" size="md"
      footer={!done && pos ? <><button className="btn-secondary" onClick={onClose}>Cancel</button>
        <button className="btn-primary" disabled={busy || !picked.length} onClick={submit}>Submit payment</button></> : undefined}>
      <ErrorBox>{error}</ErrorBox>
      {done ? <p className="text-emerald-700">{done} You can sign in once Zentrix confirms it.</p> : !pos ? (error ? null : <Spinner />) : (
        <div className="grid gap-4 md:grid-cols-2">
          <div className="text-center">
            {pos.qr_image ? <img src={pos.qr_image} alt="UPI QR" className="mx-auto w-56" /> : <p className="text-sm text-slate-500">No QR uploaded yet</p>}
            {pos.upi_id && <p className="mt-2 font-mono">{pos.upi_id}</p>}
            <p className="text-sm text-slate-500">{pos.payee_name}</p>
            {pos.upi_link && <a className="btn-secondary mt-2" href={pos.upi_link}>Open UPI app ({formatINR(amount)})</a>}
            {pos.bank_details && <p className="mt-2 whitespace-pre-line text-xs text-slate-500">{pos.bank_details}</p>}
          </div>
          <div>
            <p className="mb-2 font-semibold">{pos.shop_name} owes {formatINR(pos.amount_due_paise)}</p>
            {pos.invoices.map((i) => (
              <label key={i.id} className="flex items-center gap-2 py-1">
                <input type="checkbox" checked={picked.includes(i.id)} disabled={i.status === 'submitted'}
                  onChange={(e) => setPicked(e.target.checked ? [...picked, i.id] : picked.filter((x) => x !== i.id))} />
                {i.period_label} — {formatINR(i.amount_paise)} {i.status === 'submitted' && <span className="text-xs text-amber-700">(under review)</span>}
              </label>
            ))}
            <p className="my-2 text-lg font-bold">Paying {formatINR(amount)}</p>
            <Field label="UPI reference / UTR"><input className="input" value={utr} onChange={(e) => setUtr(e.target.value)} /></Field>
            <Field label="Payment screenshot" className="mt-2">
              <input type="file" accept="image/*,application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </Field>
          </div>
        </div>
      )}
    </Modal>
  );
}
